import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import { useApolloClient, ApolloError } from '@apollo/client';
import { Box, Typography, CircularProgress, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Refresh as RefreshIcon, Error as ErrorIcon } from '@material-ui/icons';
import { markPollRefresh } from '@/hooks/useScheduleSubscription';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(3),
  },
  errorIcon: {
    fontSize: '4rem',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
  loadingIcon: {
    color: theme.palette.primary.main,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: theme.spacing(1),
  },
  message: {
    fontSize: '1rem',
    color: '#546e7a',
    textAlign: 'center',
    maxWidth: '500px',
    lineHeight: 1.6,
  },
  countdown: {
    color: '#78909c',
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  retryButton: {
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  reloadButton: {
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    borderRadius: '8px',
  },
  errorDetails: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    maxWidth: '600px',
    width: '100%',
    alignSelf: 'center',
  },
  errorCode: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: '#856404',
    backgroundColor: '#fff',
    padding: theme.spacing(1.5),
    borderRadius: '4px',
    marginTop: theme.spacing(1),
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

interface SubscriptionErrorHandlerProps {
  /** Loading state from subscription */
  loading: boolean;
  /** Error from subscription */
  error?: ApolloError | Error | null;
  /** Callback to trigger refetch */
  onRetry?: () => void;
  /** Maximum automatic retry attempts (default: 3, use Infinity for kiosk mode) */
  maxAutoRetries?: number;
  /** Initial retry delay in seconds (default: 5) */
  initialDelay?: number;
  /** Maximum retry delay in seconds (default: 300 = 5 minutes) */
  maxDelay?: number;
  /** Show detailed error message (default: false) */
  showErrorDetails?: boolean;
  /** Enable infinite retries for TV displays (default: false) */
  isKioskMode?: boolean;
  /** Content to render when no error */
  children: ReactNode;
}

/**
 * Smart error handler for Apollo subscriptions
 * Replaces the old Refresh component that did full page reloads
 *
 * Features:
 * - Auto-retries with exponential backoff
 * - Shows countdown timer
 * - Manual retry button
 * - Only reloads page as last resort
 * - Uses Apollo's refetchQueries instead of full reload
 * - Preserves component state
 * - Supports infinite retries for kiosk/TV displays
 *
 * @example
 * ```tsx
 * <SubscriptionErrorHandler
 *   loading={subscription.loading}
 *   error={subscription.error}
 *   onRetry={handleRetry}
 *   isKioskMode={true}
 * >
 *   <YourComponent />
 * </SubscriptionErrorHandler>
 * ```
 */
const SubscriptionErrorHandler: React.FC<SubscriptionErrorHandlerProps> = ({
  loading,
  error,
  onRetry,
  maxAutoRetries = 3,
  initialDelay = 5,
  maxDelay = 300, // 5 minutes max for kiosk mode
  showErrorDetails = false,
  isKioskMode = false,
  children,
}) => {
  const classes = useStyles();
  const client = useApolloClient();
  const [retryCount, setRetryCount] = useState<number>(0);
  const [countdown, setCountdown] = useState<number>(initialDelay);
  const [isAutoRetrying, setIsAutoRetrying] = useState<boolean>(false);

  // Calculate delay with exponential backoff: 5s, 10s, 20s, 40s, 80s, 160s, 300s (max)
  // For kiosk mode, cap at maxDelay to avoid waiting too long
  const getRetryDelay = useCallback(
    (attempt: number): number => {
      const exponentialDelay = initialDelay * Math.pow(2, attempt);
      return Math.min(exponentialDelay, maxDelay);
    },
    [initialDelay, maxDelay]
  );

  // Determine effective max retries (Infinity for kiosk mode)
  // This ensures kiosks never stop trying to reconnect
  const effectiveMaxRetries = isKioskMode ? Infinity : maxAutoRetries;

  // Auto-retry logic with exponential backoff
  useEffect(() => {
    if (error && retryCount < effectiveMaxRetries) {
      const delay = getRetryDelay(retryCount);
      setCountdown(delay);
      setIsAutoRetrying(true);

      // Countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Retry after delay
      const retryTimeout = setTimeout(() => {
        const attemptInfo = isKioskMode
          ? `attempt ${retryCount + 1} (infinite mode)`
          : `attempt ${retryCount + 1}/${maxAutoRetries}`;
        console.log(`[SubscriptionErrorHandler] Auto-retry ${attemptInfo}`);
        setRetryCount(prev => prev + 1);
        setIsAutoRetrying(false);

        // Trigger refetch - mark poll refresh to prevent stale subscription events
        markPollRefresh();
        if (onRetry) {
          onRetry();
        } else {
          // Default: refetch all active queries
          client.refetchQueries({ include: 'active' }).catch(err => {
            console.error('[SubscriptionErrorHandler] Refetch failed:', err);
          });
        }
      }, delay * 1000);

      return () => {
        clearInterval(countdownInterval);
        clearTimeout(retryTimeout);
      };
    }
  }, [error, retryCount, effectiveMaxRetries, maxAutoRetries, client, onRetry, getRetryDelay, isKioskMode]);

  // Reset retry count when error clears
  useEffect(() => {
    if (!error && retryCount > 0) {
      console.log('[SubscriptionErrorHandler] Error cleared, resetting retry count');
      setRetryCount(0);
      setIsAutoRetrying(false);
    }
  }, [error, retryCount]);

  // Manual retry handler
  const handleManualRetry = useCallback(() => {
    console.log('[SubscriptionErrorHandler] Manual retry triggered');
    setRetryCount(0);
    setIsAutoRetrying(false);

    // Mark poll refresh to prevent stale subscription events
    markPollRefresh();
    if (onRetry) {
      onRetry();
    } else {
      client.refetchQueries({ include: 'active' }).catch(err => {
        console.error('[SubscriptionErrorHandler] Manual refetch failed:', err);
      });
    }
  }, [client, onRetry]);

  // Full page reload as last resort
  const handleForceReload = useCallback(() => {
    console.warn('[SubscriptionErrorHandler] Force reload triggered');
    window.location.reload();
  }, []);

  // Loading state (only show if no error)
  if (loading && !error) {
    return (
      <Box className={classes.container}>
        <CircularProgress size={60} className={classes.loadingIcon} />
        <Typography variant="h6">Loading schedule...</Typography>
        <Typography variant="body2" color="textSecondary">
          Fetching procedures, rooms, and staff data
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    const hasExhaustedRetries = retryCount >= effectiveMaxRetries;
    const isDevelopment = (import.meta as any).env?.MODE === 'development';

    // Extract user-friendly error message
    const getUserFriendlyMessage = () => {
      const errorMsg = error.message || '';

      // GraphQL schema errors (like missing fields)
      if (errorMsg.includes('Cannot query field')) {
        return 'The schedule screen needs to be updated. Please contact support.';
      }

      // Network errors
      if (errorMsg.includes('NetworkError') || errorMsg.includes('Failed to fetch')) {
        return 'Unable to connect to the server. Please check your internet connection.';
      }

      // Generic fallback
      return "We're having trouble loading the schedule. The system will retry automatically.";
    };

    // Extract error code for support (first 100 chars of error message)
    const getErrorCode = () => {
      const errorMsg = error.message || '';
      return errorMsg.substring(0, 100) + (errorMsg.length > 100 ? '...' : '');
    };

    return (
      <Box className={classes.container}>
        <ErrorIcon className={classes.errorIcon} />

        <Typography className={classes.title}>
          {isKioskMode ? 'Reconnecting...' : hasExhaustedRetries ? 'Unable to Load Schedule' : 'Temporary Issue'}
        </Typography>

        <Typography className={classes.message}>{getUserFriendlyMessage()}</Typography>

        {isAutoRetrying && (
          <Typography className={classes.countdown}>
            {isKioskMode && retryCount >= 5
              ? `Next retry in ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`
              : `Retrying in ${countdown} second${countdown !== 1 ? 's' : ''}...`}
          </Typography>
        )}

        {!isKioskMode && (
          <Box className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleManualRetry}
              disabled={isAutoRetrying}
              className={classes.retryButton}
            >
              {isAutoRetrying ? 'Retrying...' : 'Retry Now'}
            </Button>

            {hasExhaustedRetries && (
              <Button variant="outlined" onClick={handleForceReload} className={classes.reloadButton}>
                Reload Page
              </Button>
            )}
          </Box>
        )}

        {(showErrorDetails || isDevelopment) && error.message && (
          <Box className={classes.errorDetails}>
            <Typography variant="body2" style={{ fontWeight: 600, color: '#856404', marginBottom: '8px' }}>
              Error Code (for support):
            </Typography>
            <Typography className={classes.errorCode}>{getErrorCode()}</Typography>
            <Typography variant="caption" style={{ color: '#856404', marginTop: '8px', display: 'block' }}>
              Please share this code with support if the issue persists.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Success state - render children
  return <>{children}</>;
};

export default SubscriptionErrorHandler;
